import axios from 'axios';
import { Auth } from '@aws-amplify/auth';
import { getSelectedCompany } from 'src/utils/company-storage';

// Get the API URL from environment variables or use a default
const API_BASE_URL = import.meta.env.VITE_API_URI || 'http://localhost:8000';

async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};

  try {
    const session = await Auth.currentSession();
    const accessToken = session.getAccessToken().getJwtToken();

    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }

    const selectedCompany = getSelectedCompany();
    if (selectedCompany?.id) {
      headers['X-Selected-Company-Id'] = selectedCompany.id;
    }
  } catch (error) {
    console.warn('Error getting billing auth headers:', error);
  }

  return headers;
}

/**
 * Creates a Shopify billing subscription
 * @param billingPlanCode - The code of the billing plan (e.g., "Basic_Annual")
 * @param shop - The Shopify store domain
 * @param isOnboarding - Flag indicating if this is part of the onboarding flow
 * @returns Promise with subscription data including confirmation URL
 */
export async function createBillingSubscription(
  billingPlanCode: string,
  shop: string,
  isOnboarding: boolean = true
) {
  try {
    const authHeaders = await getAuthHeaders();
    const response = await axios.post(
      `${API_BASE_URL}/billing/create-subscription`,
      {
        billing_plan_code: billingPlanCode, // Backend still expects this parameter name
        shop,
        is_onboarding: isOnboarding,
      },
      {
        headers: authHeaders,
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error creating billing subscription:', error);
    throw error;
  }
}

/**
 * Validates subscription status for a company
 * @param companyId - The ID of the company to check subscription for
 * @returns Promise with subscription validation data
 * @throws Error when the subscription validation API call fails
 */
export async function validateSubscription(companyId: string) {
  try {
    const authHeaders = await getAuthHeaders();
    const response = await axios.post(
      `${API_BASE_URL}/validate-subscription`,
      {
        company_id: companyId,
      },
      {
        headers: authHeaders,
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error validating subscription:', error);
    // Re-throw the error so the caller can decide how to handle it
    // This prevents incorrect assumptions about subscription status
    throw error;
  }
}
